import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  MessageSquare, Plus, Search, Pin, Lock, Filter, 
  ChevronRight, Clock, MessageCircle, X, Image as ImageIcon, ChevronDown, ChevronUp, HelpCircle
} from "lucide-react";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ImageUpload from "@/components/ImageUpload";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const REACTION_EMOJIS = ["🐟", "🐠", "🦈", "🐡", "🐙", "🦑", "🐚", "🦀", "🐳", "🐬", "🦭", "🪸", "🤿", "👍", "❤️", "😂", "😮", "🔥"];

// Formatting Cheatsheet Component
const FormattingHelp = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold text-lg text-gray-900">Formatting Cheatsheet</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Text Formatting</h4>
            <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>**bold**</code> → <strong>bold</strong></p>
              <p><code>*italic*</code> → <em>italic</em></p>
              <p><code>`code`</code> → <code className="bg-gray-200 px-1 rounded">code</code></p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Colors</h4>
            <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>{'{color:red}text{/color}'}</code> → <span className="text-red-500">text</span></p>
              <p className="text-gray-500 text-xs mt-1">Colors: red, blue, green, orange, purple, pink, cyan, teal, yellow, gray, navy, lime, gold</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Links</h4>
            <div className="bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>[link text](https://url)</code></p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Images (Inline)</h4>
            <div className="space-y-1.5 bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>![alt](#0)</code> → 1st uploaded image</p>
              <p><code>![alt](#1|center)</code> → 2nd image, centered</p>
              <p><code>![alt](#2|right)</code> → 3rd image, right-aligned</p>
              <p className="text-gray-500 text-xs mt-1">Positions: left (default), center, right</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-cyan-700 mb-2">Lists</h4>
            <div className="bg-gray-50 p-3 rounded-lg font-mono text-xs">
              <p><code>- Item 1</code></p>
              <p><code>- Item 2</code></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Forum = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThread, setNewThread] = useState({ title: "", content: "", tags: [], images: [] });
  const [creating, setCreating] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [sortBy, setSortBy] = useState("recent"); // recent, votes, fish
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      // Redirect dive centers away
      if (parsedUser.role === "dive_center") {
        navigate("/");
        return;
      }
      setUser(parsedUser);
    } else {
      navigate("/account");
    }
  }, [navigate]);

  useEffect(() => {
    fetchTags();
    fetchThreads();
  }, [selectedTag, searchQuery]);

  const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/forum/tags`);
      setTags(response.data.tags);
    } catch (err) {
      console.error("Failed to fetch tags:", err);
    }
  };

  const fetchThreads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedTag) params.append("tag", selectedTag);
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await axios.get(`${API}/forum/threads?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setThreads(response.data.threads);
    } catch (err) {
      console.error("Failed to fetch threads:", err);
    } finally {
      setLoading(false);
    }
  };

  // Sort threads based on selected criteria
  const getSortedThreads = () => {
    if (!threads.length) return [];
    
    const sorted = [...threads];
    
    // Pinned threads always go first
    const pinned = sorted.filter(t => t.is_pinned);
    const unpinned = sorted.filter(t => !t.is_pinned);
    
    switch (sortBy) {
      case "votes":
        // Sort by total reactions count
        unpinned.sort((a, b) => {
          const countA = Object.values(a.reactions || {}).reduce((sum, arr) => sum + arr.length, 0);
          const countB = Object.values(b.reactions || {}).reduce((sum, arr) => sum + arr.length, 0);
          return countB - countA;
        });
        break;
      case "fish":
        // Sort by fish emoji reactions (🐟🐠🦈🐡🐙🦑🐚🦀🐳🐬🦭🪸🤿)
        const fishEmojis = ["🐟", "🐠", "🦈", "🐡", "🐙", "🦑", "🐚", "🦀", "🐳", "🐬", "🦭", "🪸", "🤿"];
        unpinned.sort((a, b) => {
          const countA = fishEmojis.reduce((sum, emoji) => sum + (a.reactions?.[emoji]?.length || 0), 0);
          const countB = fishEmojis.reduce((sum, emoji) => sum + (b.reactions?.[emoji]?.length || 0), 0);
          return countB - countA;
        });
        break;
      case "recent":
      default:
        // Sort by last_activity (most recent first)
        unpinned.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
        break;
    }
    
    return [...pinned, ...unpinned];
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newThread.title.trim() || !newThread.content.trim()) return;

    try {
      setCreating(true);
      await axios.post(`${API}/forum/threads`, newThread, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setShowNewThread(false);
      setNewThread({ title: "", content: "", tags: [], images: [] });
      fetchThreads();
    } catch (err) {
      console.error("Failed to create thread:", err);
      alert(err.response?.data?.detail || "Failed to create thread");
    } finally {
      setCreating(false);
    }
  };

  const toggleTag = (tag) => {
    setNewThread(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const getTotalReactions = (reactions) => {
    return Object.values(reactions || {}).reduce((sum, users) => sum + users.length, 0);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div className="min-h-screen">
      <SEO 
        title="Forum - George Dives Community"
        description="Join discussions with fellow divers"
      />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Header */}
      <section className="pt-32 pb-8 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                Community Forum
              </h1>
              <p className="text-white/90">
                Discuss, share, and connect with fellow divers
              </p>
            </div>
            <Button
              onClick={() => setShowNewThread(true)}
              className="bg-white text-cyan-600 hover:bg-gray-100"
              data-testid="new-thread-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Thread
            </Button>
          </div>
        </div>
      </section>

      {/* Filters — collapsible */}
      <section className="border-b sticky top-24 z-30 backdrop-blur-xl" style={{ backgroundColor: 'var(--page-bg, #ffffff)' }}>
        <div className="container mx-auto px-4">
          <button 
            onClick={() => setTagsExpanded(!tagsExpanded)}
            className="w-full flex items-center justify-between py-3"
            data-testid="forum-filters-toggle"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-semibold">Filters</span>
              {(selectedTag || searchQuery) && (
                <span className="px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full">Active</span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${tagsExpanded ? 'rotate-180' : ''}`} />
          </button>
          <div className={`overflow-hidden transition-all duration-300 ${tagsExpanded ? 'max-h-96 pb-4' : 'max-h-0'}`}>
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search threads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            {/* Tags */}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    !selectedTag 
                      ? "bg-cyan-500 text-white" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  All
                </button>
                {tags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTag === tag 
                        ? "bg-cyan-500 text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Sort dropdown */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500 hidden sm:inline">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                data-testid="sort-select"
              >
                <option value="recent">Recent Activity</option>
                <option value="votes">Most Reactions</option>
                <option value="fish">Fish Count 🐟</option>
              </select>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Thread List */}
      <section className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading threads...</p>
            </div>
          ) : getSortedThreads().length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No threads yet</h3>
              <p className="text-gray-500 mb-4">Be the first to start a discussion!</p>
              <Button onClick={() => setShowNewThread(true)} className="bg-cyan-500 hover:bg-cyan-600">
                <Plus className="w-4 h-4 mr-2" />
                Create Thread
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {getSortedThreads().map(thread => (
                <Card 
                  key={thread.id}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    thread.is_pinned ? "border-l-4 border-l-cyan-500" : ""
                  }`}
                  onClick={() => navigate(`/community/forum/${thread.id}`)}
                  data-testid={`thread-${thread.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Author Avatar - clickable to view profile */}
                      <div 
                        className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/profile/${thread.author_id}`);
                        }}
                        title={`View ${thread.author_name}'s profile`}
                      >
                        {thread.author_profile_pic ? (
                          <img 
                            src={getImageUrl(thread.author_profile_pic)} 
                            alt={thread.author_name}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-transparent hover:ring-cyan-400 transition-all"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold ring-2 ring-transparent hover:ring-cyan-400 transition-all">
                            {thread.author_name?.charAt(0) || "?"}
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {thread.is_pinned && (
                            <Pin className="w-4 h-4 text-cyan-500" />
                          )}
                          {thread.is_locked && (
                            <Lock className="w-4 h-4 text-gray-400" />
                          )}
                          <h3 className="font-semibold text-gray-900 truncate">
                            {thread.title}
                          </h3>
                        </div>
                        
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {thread.content}
                        </p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{thread.author_name}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(thread.last_activity)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {thread.reply_count} replies
                          </span>
                          {getTotalReactions(thread.reactions) > 0 && (
                            <span>
                              {Object.keys(thread.reactions).slice(0, 3).join("")} {getTotalReactions(thread.reactions)}
                            </span>
                          )}
                        </div>
                        
                        {/* Tags */}
                        {thread.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {thread.tags.map(tag => (
                              <span 
                                key={tag}
                                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* New Thread Modal */}
      {showNewThread && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Create New Thread</CardTitle>
              <button onClick={() => setShowNewThread(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateThread} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newThread.title}
                    onChange={(e) => setNewThread(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="What's your thread about?"
                    required
                    data-testid="thread-title-input"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="content">Content</Label>
                    <button
                      type="button"
                      onClick={() => setShowFormattingHelp(true)}
                      className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      Formatting help
                    </button>
                  </div>
                  <textarea
                    id="content"
                    value={newThread.content}
                    onChange={(e) => setNewThread(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Share your thoughts, questions, or experiences... (supports **bold**, *italic*, {color:blue}colors{/color}, and more)"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                    required
                    data-testid="thread-content-input"
                  />
                </div>
                
                <div>
                  <Label>Tags (optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          newThread.tags.includes(tag)
                            ? "bg-cyan-500 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Images (optional)
                  </Label>
                  <div className="mt-2">
                    <ImageUpload
                      uploadType="forum"
                      multiple={true}
                      currentImages={newThread.images}
                      onImagesUpdated={(images) => setNewThread(prev => ({ ...prev, images }))}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowNewThread(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-cyan-500 hover:bg-cyan-600"
                    disabled={creating}
                    data-testid="create-thread-btn"
                  >
                    {creating ? "Creating..." : "Create Thread"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Formatting Help Modal */}
      <FormattingHelp isOpen={showFormattingHelp} onClose={() => setShowFormattingHelp(false)} />

      <Footer />
    </div>
  );
};

export default Forum;
